const MIN_DIGITS = 7;
const MAX_DIGITS = 15;
function formatPhoneForKlaviyo(phoneNumber, defaultCountryCode = "1") {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return { isValid: true };
  }
  const trimmed = phoneNumber.trim();
  const cleaned = trimmed.replace(/^tel:\s*/, "");
  const digitsOnly = cleaned.replace(/\D/g, "");
  if (digitsOnly.length < MIN_DIGITS || digitsOnly.length > MAX_DIGITS) {
    return {
      isValid: false,
      error: `Phone number must contain ${MIN_DIGITS}-${MAX_DIGITS} digits. Example: +12345678901`
    };
  }
  let formatted = cleaned;
  if (!cleaned.startsWith("+")) {
    if (digitsOnly.length === 10) {
      formatted = `+${defaultCountryCode}${cleaned}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      formatted = `+${cleaned}`;
    } else {
      formatted = `+${cleaned}`;
    }
  }
  const validChars = /^\+[\d\s\-]+$/;
  if (!validChars.test(formatted)) {
    return {
      isValid: false,
      error: "Phone number contains invalid characters. Use only digits, spaces, dashes, and +"
    };
  }
  const formattedDigits = formatted.replace(/\D/g, "");
  if (formattedDigits.length < MIN_DIGITS || formattedDigits.length > MAX_DIGITS) {
    return {
      isValid: false,
      error: `Invalid phone number length. Example: +12345678901`
    };
  }
  return {
    isValid: true,
    formatted
  };
}
const FIELD_VARIATIONS = {
  email: [
    "email",
    "email-address",
    "emailaddress",
    "email address",
    "email_address"
  ],
  firstName: [
    "first_name",
    "first name",
    "first-name",
    "firstname"
  ],
  lastName: [
    "last_name",
    "last name",
    "last-name",
    "lastname"
  ],
  phoneNumber: [
    "phone_number",
    "phone-number",
    "phone",
    "telephone",
    "tel",
    "mobile"
  ]
};
function getFieldValue(formObject, fieldType) {
  const variations = FIELD_VARIATIONS[fieldType];
  for (const variation of variations) {
    for (const [key, value] of Object.entries(formObject)) {
      if (key.toLowerCase() === variation && value && typeof value === "string") {
        return value.toString();
      }
    }
  }
  return "";
}
function mapFormFields(formObject) {
  const phoneValue = getFieldValue(formObject, "phoneNumber");
  const phoneResult = phoneValue ? formatPhoneForKlaviyo(phoneValue) : { isValid: true, formatted: "" };
  return {
    email: getFieldValue(formObject, "email"),
    first_name: getFieldValue(formObject, "firstName"),
    last_name: getFieldValue(formObject, "lastName"),
    phone_number: phoneResult.formatted || "",
    phone_validation: phoneResult
  };
}
function removeFieldsFromProperties(formObject, mappedFields) {
  const cleanedProperties = { ...formObject };
  for (const [key, value] of Object.entries(formObject)) {
    const keyLower = key.toLowerCase();
    if (mappedFields.email && FIELD_VARIATIONS.email.includes(keyLower) || mappedFields.first_name && FIELD_VARIATIONS.firstName.includes(keyLower) || mappedFields.last_name && FIELD_VARIATIONS.lastName.includes(keyLower) || mappedFields.phone_number && FIELD_VARIATIONS.phoneNumber.includes(keyLower)) {
      delete cleanedProperties[key];
    }
  }
  return cleanedProperties;
}
async function subscribeToKlaviyo({
  email,
  formData,
  companyId,
  listIds
}) {
  const listsArray = Array.isArray(listIds) ? listIds : [listIds];
  const mappedFields = mapFormFields(formData);
  if (mappedFields.phone_validation && !mappedFields.phone_validation.isValid) {
    throw new Error(`Phone validation failed: ${mappedFields.phone_validation.error}`);
  }
  const cleanedProperties = removeFieldsFromProperties(formData, mappedFields);
  for (const listId of listsArray) {
    if (!listId) continue;
    const profileAttributes = {
      properties: cleanedProperties,
      email: mappedFields.email,
      first_name: mappedFields.first_name,
      last_name: mappedFields.last_name
    };
    if (mappedFields.phone_number && mappedFields.phone_number.trim() !== "") {
      profileAttributes.phone_number = mappedFields.phone_number;
    }
    const klaviyoBody = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: { data: { type: "list", id: listId } }
        }
      }
    };
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${companyId}`,
      {
        method: "POST",
        headers: {
          "content-type": "application/vnd.api+json",
          REVISION: "2025-04-15"
        },
        body: JSON.stringify(klaviyoBody)
      }
    );
    if (!response.ok) {
      let errorMessage = "Subscription failed";
      try {
        const errorData = await response.json();
        if (errorData.errors && errorData.errors.length > 0) {
          const error = errorData.errors[0];
          errorMessage = error.detail || error.title || "Subscription failed";
          if (error.detail && error.detail.includes("phone number")) {
            errorMessage = error.detail;
          }
        }
      } catch (parseError) {
        errorMessage = `Subscription failed (${response.status})`;
      }
      throw new Error(errorMessage);
    }
  }
}
export {
  formatPhoneForKlaviyo as f,
  getFieldValue as g,
  subscribeToKlaviyo as s
};
